import { useEffect, useRef, useState } from 'react'

import cx from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useInView } from 'react-intersection-observer'

import { DropdownNav, DropdownNavLink } from '@/components/Dropdown'

import { NavLink } from './Header'

import styles from './DesktopNavMenu.module.scss'

const DesktopLink: React.FC<{
  link: NavLink
  isActive: boolean
  show: boolean
  rootElement?: Element
  linkInView: (link: NavLink, inView: boolean) => void
}> = ({ link, isActive, show, linkInView, rootElement }) => {
  const { ref } = useInView({
    initialInView: true,
    onChange: (inView) => linkInView(link, inView),
    threshold: 1,
    root: rootElement,
  })

  return (
    <div ref={ref} key={link.href} className={cx(styles.Link, { [styles.Active]: isActive, [styles.InView]: show })}>
      <Link href={link.href} shallow={false}>
        {link.label}
      </Link>
    </div>
  )
}

const reduceNavLinkToMap = (links: NavLink[], maxHeaderLinks: number) =>
  links.reduce<LinkMap>((prev, curr, index) => {
    return { ...prev, [curr.href]: { ...curr, isVisible: index < maxHeaderLinks, index } }
  }, {})

type LinkMap = {
  [key: string]: NavLink & { isVisible: boolean; index: number }
}

export const DesktopNavMenu = ({ primary, isLoggedIn }: { primary: NavLink[]; isLoggedIn: boolean }) => {
  const maxHeaderLinks = 4
  const [links, setLinks] = useState<LinkMap>(reduceNavLinkToMap(primary, maxHeaderLinks))

  useEffect(() => {
    const mappedLinks = reduceNavLinkToMap(primary, maxHeaderLinks)
    const newLinks = Object.keys(mappedLinks).reduce<LinkMap>((prev, curr) => {
      return { ...prev, [curr]: { ...mappedLinks[curr], isVisible: links[curr]?.isVisible } }
    }, {})
    setLinks(newLinks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary])

  const ref = useRef<HTMLDivElement>(null)
  const { asPath } = useRouter()

  const dropdownInViewHandler = (inView: boolean) => {
    if (inView) return
    const allLinks = Object.values(links)
    const visibleLinks = allLinks.filter((link) => link.isVisible)
    const hideLinks = visibleLinks.sort((a, b) => b.index - a.index).slice(0, 1)

    if (hideLinks.length === 0) return

    setLinks((state) => {
      const newLinks = Object.fromEntries(hideLinks.map((link) => [link.href, { ...link, isVisible: false }]))
      return { ...state, ...newLinks }
    })
  }

  const linkInViewHandler = (link: NavLink, inView: boolean) => {
    setLinks((state) => {
      if (inView && state[link.href].index >= maxHeaderLinks) return state
      return { ...state, [link.href]: { ...state[link.href], isVisible: inView } }
    })
  }

  const sortedLinks = Object.values(links).sort((a, b) => a.index - b.index)
  const visibleLinks = sortedLinks.filter((link) => link.isVisible)
  const nextLink = sortedLinks.length > visibleLinks.length ? sortedLinks[visibleLinks.length] : undefined

  return (
    <nav className={cx(styles.DesktopNavMenu, { [styles.LoggedIn]: isLoggedIn })} ref={ref} data-cy="desktop-nav-menu">
      {visibleLinks.map((link) => (
        <DesktopLink
          rootElement={ref.current ?? undefined}
          key={link.href}
          link={link}
          isActive={link.href.includes(asPath) && asPath !== '/'}
          linkInView={linkInViewHandler}
          show={true}
        />
      ))}
      {nextLink && (
        <DesktopLink
          rootElement={ref.current ?? undefined}
          key={nextLink.href}
          link={nextLink}
          isActive={nextLink.href.includes(asPath) && asPath !== '/'}
          linkInView={linkInViewHandler}
          show={false}
        />
      )}
      <DropdownNav
        rootElement={ref.current ?? undefined}
        className={cx(styles.Dropdown, styles.DropdownVisible)}
        inViewChanged={dropdownInViewHandler}
        buttonContent={
          <div className={styles.DropdownIcon}>
            <span />
            <span />
            <span />
          </div>
        }
      >
        {Object.values(links)
          .filter((l) => !l.isVisible)
          .map(({ href, label }, i) => (
            <DropdownNavLink key={label} href={href} isActive={href.includes(asPath) && asPath !== '/'} index={i}>
              {label}
            </DropdownNavLink>
          ))}
      </DropdownNav>
    </nav>
  )
}
